import { createContext, useContext } from "react";

import { loadEnv } from "@/config/env";
import { KEYS } from "@/data/hooks";
import { invalidate, useResource, type Resource } from "@/data/resource";
import { syncConfig } from "@/data/sync/config";
import { devParams } from "@/data/sync/url";

import { createLiveFilesApi } from "./live";
import { createLocalFilesApi } from "./local";
import { indexedDbStore } from "./local-store";
import { xhrPut } from "./put";
import type { ProjectFile, ProjectFilesApi, ProjectFolder, TrashedFile } from "./types";

export * from "./types";
export { checkName } from "./local";

/**
 * The app's project files: the doc service's in live mode, this browser's in local mode (`VITE_FILES_MODE`, which
 * follows `VITE_SYNC_MODE` when unset). Pages and dialogs use `useFilesApi()` and never know which.
 */

export interface FilesSettings {
  readonly live: boolean;
  /** The largest request the app sends to project files; Draft's pickers and saves stay within it. */
  readonly maxUploadBytes: number;
  /** The largest file the Files page offers to upload: bigger in live mode, which sends big files to storage. */
  readonly maxFileBytes: number;
}

const MS_PER_DAY = 86_400_000;

let settings: FilesSettings | undefined;
let api: ProjectFilesApi | undefined;

export function filesSettings(): FilesSettings {
  if (settings !== undefined) return settings;
  const env = loadEnv();
  const live = (env.VITE_FILES_MODE ?? env.VITE_SYNC_MODE) === "live";
  settings = {
    live,
    maxUploadBytes: env.VITE_FILES_MAX_UPLOAD_BYTES,
    maxFileBytes: live ? env.VITE_FILES_MAX_BIG_UPLOAD_BYTES : env.VITE_FILES_MAX_UPLOAD_BYTES,
  };
  return settings;
}

/** The app's project files backend, made on first use. */
export function defaultFilesApi(): ProjectFilesApi {
  if (api !== undefined) return api;
  const { live, maxUploadBytes } = filesSettings();
  const env = loadEnv();
  api = live
    ? createLiveFilesApi({
        apiPath: env.VITE_FILES_API_PATH,
        fetch: (input, init) => fetch(input, init),
        dev: devParams(window.location.search, import.meta.env.DEV),
        devUserParam: syncConfig.devUserParam,
        devOrgParam: syncConfig.devOrgParam,
        maxUploadBytes,
        maxBigUploadBytes: env.VITE_FILES_MAX_BIG_UPLOAD_BYTES,
        put: xhrPut,
        bigUploads: {
          partsInFlight: env.VITE_FILES_UPLOAD_PARTS_IN_FLIGHT,
          retries: env.VITE_FILES_UPLOAD_PART_RETRIES,
          retryDelayMs: env.VITE_FILES_UPLOAD_RETRY_DELAY_MS,
        },
        sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
      })
    : createLocalFilesApi({
        store: indexedDbStore(),
        maxUploadBytes,
        trashRetentionMs: env.VITE_FILES_TRASH_RETENTION_DAYS * MS_PER_DAY,
        now: () => new Date(),
        newId: () => crypto.randomUUID(),
      });
  return api;
}

/** Another backend for the components below it (tests). */
export const FilesApiContext = createContext<ProjectFilesApi | undefined>(undefined);

export function useFilesApi(): ProjectFilesApi {
  return useContext(FilesApiContext) ?? defaultFilesApi();
}

/** Cache keys: a project's files (the trailing slash keeps one project's refresh off another's). */
const filesPrefix = (projectId: string): string => `files/${projectId}/`;
export const filesKey = (projectId: string): string => `${filesPrefix(projectId)}list`;

/** A project's files, kept fresh by `refreshFiles` after every change. */
export function useProjectFiles(projectId: string): Resource<ProjectFile[]> {
  const files = useFilesApi();
  return useResource(filesKey(projectId), () => files.list(projectId));
}

/** A project's folders, refreshed with its files. */
export function useProjectFolders(projectId: string): Resource<ProjectFolder[]> {
  const files = useFilesApi();
  return useResource(`${filesPrefix(projectId)}folders`, () => files.folders(projectId));
}

export const trashKey = (projectId: string): string => `${filesPrefix(projectId)}trash`;

/** A project's Trash, kept fresh by `refreshFiles` after every change. */
export function useProjectTrash(projectId: string): Resource<TrashedFile[]> {
  const files = useFilesApi();
  return useResource(trashKey(projectId), () => files.trash(projectId));
}

/** How many files the project holds as this person sees them: the project page's count (sidebar, first steps). */
export async function countProjectFiles(projectId: string): Promise<number> {
  return (await defaultFilesApi().list(projectId)).length;
}

/** Every view of a project's files, and its Trash, loads again, and so does the project, whose page counts them. */
export function refreshFiles(projectId: string): void {
  invalidate(filesPrefix(projectId));
  invalidate(KEYS.project(projectId));
}
